import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'fieldidx-cookie-consent';

type ConsentValue = 'accepted' | 'rejected' | null;

export function CookieConsentBanner() {
  const [consent, setConsent] = useState<ConsentValue>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const storedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!storedConsent) {
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    setConsent('accepted');
    setIsVisible(false);
  };

  const handleReject = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'rejected');
    setConsent('rejected');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-in slide-in-from-bottom duration-300">
      <div className="container mx-auto max-w-4xl">
        <div className="bg-card border border-border rounded-xl shadow-lg p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-shrink-0 hidden sm:block">
              <div className="w-10 h-10 rounded-full bg-teal-light flex items-center justify-center">
                <Cookie className="h-5 w-5 text-teal" />
              </div>
            </div>
            
            <div className="flex-1 space-y-2">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Cookie className="h-4 w-4 text-teal sm:hidden" />
                FieldIDX Uses Cookies to Power Your NIL Valuation
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground">
                We use cookies to secure your account and improve our AI valuation models. 
                By clicking "Accept All," you agree to our use of cookies for analytics and NIL opportunity matching.{' '}
                <Link to="/cookie-policy" className="text-teal hover:underline">
                  Cookie Policy
                </Link>
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={handleReject}
                className="text-xs sm:text-sm order-2 sm:order-1"
              >
                Reject
              </Button>
              <Button
                size="sm"
                onClick={handleAccept}
                variant="teal"
                className="text-xs sm:text-sm order-1 sm:order-2 font-semibold"
              >
                Accept All
              </Button>
            </div>

            <button
              onClick={handleReject}
              className="absolute top-2 right-2 sm:hidden text-muted-foreground hover:text-foreground"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
